import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { ILanguageConfig } from '../../interfaces/ILanguageConfig';
import { environment } from '../../../environments/environment';
import { valueIsNullOrEmpty } from '../../helper/valueIsNullOrEmpty';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private languages: ILanguageConfig[] = [];
  private languageValues: string[] = [];
  private defaultLanguage: ILanguageConfig | undefined;
  private currentLanguage: string | null = null;
  private fallbackLanguage: string = 'en';
  private baseURL: string;

  private languageChange$ = new Subject<string>();

  constructor(private http: HttpClient) {
    const suffix = environment.servicesConfig.apiUrlSuffix === '' ? '/' : `/${environment.servicesConfig.apiUrlSuffix}/`;
    const apiUrl = environment.servicesConfig.useApiURL ? environment.servicesConfig.apiUrl : location.origin;
    this.baseURL = `${apiUrl}${suffix}`;
    this.initializeLanguages();
  }

  private initializeLanguages() {
    if (environment.languageConfig && Array.isArray(environment.languageConfig)) {
      this.languages = environment.languageConfig;
      this.defaultLanguage = this.languages.find((el) => el.isDefault === true);

      if (!this.defaultLanguage && this.languages.length > 0) {
        this.defaultLanguage = this.languages[0];
      }

      this.currentLanguage = this.defaultLanguage?.value ?? this.fallbackLanguage;
    } else {
      console.warn('Language configuration is missing. Using fallback:', this.fallbackLanguage);
      this.currentLanguage = this.fallbackLanguage;
      return;
    }

    const storedLanguage = localStorage.getItem('language');
    if (!valueIsNullOrEmpty(storedLanguage)) {
      const lang = this.languages.find((el) => el.value === storedLanguage);
      if (!valueIsNullOrEmpty(lang)) {
        this.currentLanguage = lang!.value;
      }
    }

    this.languages.forEach((lang) => {
      this.languageValues.push(lang.value);
    });
  }

  GetLanguages() {
    return this.languages;
  }

  GetLanguageValues() {
    return this.languageValues;
  }

  GetCurrentLanguage(): string {
    if (this.currentLanguage === null || this.currentLanguage === undefined) {
      this.currentLanguage = this.defaultLanguage?.value ?? this.fallbackLanguage;
    }
    return this.currentLanguage;
  }

  GetDefaultLanguage(): string {
    return this.defaultLanguage?.value ?? this.fallbackLanguage;
  }

  setCurrentLanguage(language: string): boolean {
    const newLanguage = this.languages.find((el) => el.value === language);
    if (newLanguage) {
      this.currentLanguage = newLanguage.value;
      localStorage.setItem('language', newLanguage.value);
      this.languageChange$.next(this.currentLanguage);
      return true;
    }
    console.warn(`Language '${language}' not found. Available:`, this.languageValues);
    return false;
  }

  GetLanguagesForCodes(codes: string[]): ILanguageConfig[] {
    if (!codes || codes.length === 0) {
      return this.languages;
    }
    return this.languages.filter(lang => codes.includes(lang.value));
  }

  get languageChange() {
    return this.languageChange$.asObservable();
  }

  /** PUT /api/v2/user { language: "ru" } */
  sendLanguageToServer(language: string): Observable<any> {
    return this.http.put<any>(`${this.baseURL}api/v2/user`, { language });
  }
}
